import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, ArrowDown } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useSite } from "@/lib/site";
import { mediaUrl, waLink } from "@/lib/api";
import { scrollToId } from "@/lib/lenis";

const lineVariants = {
  hidden: { y: "110%" },
  show: (d) => ({ y: "0%", transition: { duration: 1, delay: d, ease: [0.16, 1, 0.3, 1] } }),
};

export const Hero = () => {
  const { t } = useI18n();
  const { data } = useSite();
  const s = data?.settings || {};
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const logoY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section
      id="inicio"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-end overflow-hidden"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        {s.hero_bg_path && (
          <img
            src={mediaUrl(s.hero_bg_path)}
            alt=""
            className="w-full h-full object-cover opacity-45"
            style={{ filter: "saturate(0.65) contrast(1.05)" }}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-ink/75 via-ink/35 to-ink" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/30 to-transparent" />
      </motion.div>

      <div className="absolute bottom-0 left-0 right-0 text-center font-display text-[19vw] leading-[0.8] text-outline select-none pointer-events-none">
        BARBERÍA
      </div>

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pb-24 sm:pb-28 pt-32 w-full grid lg:grid-cols-[1.35fr_1fr] gap-10 items-end"
      >
        <div>
          {s.hero_logo_path && (
            <motion.div
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5, duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="lg:hidden mb-8"
            >
              <img src={mediaUrl(s.hero_logo_path)} alt="Blackbeard Barbería logo" className="h-32 w-auto border border-gold/25 bg-[#050505] p-3" />
            </motion.div>
          )}

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="font-mono text-[11px] sm:text-xs tracking-[0.3em] text-gold mb-6"
          >
            {t("hero.eyebrow")}
          </motion.p>

          <h1 className="font-display leading-[0.86] text-[clamp(3.4rem,12.5vw,11rem)] text-bone">
            <span className="block overflow-hidden">
              <motion.span custom={0.35} variants={lineVariants} initial="hidden" animate="show" className="block">
                BLACK
              </motion.span>
            </span>
            <span className="block overflow-hidden text-gold">
              <motion.span custom={0.5} variants={lineVariants} initial="hidden" animate="show" className="block">
                BEARD
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-6 max-w-md text-mut text-base sm:text-lg"
          >
            {t("hero.sub")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.8 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <a
              data-testid="hero-whatsapp-button"
              href={waLink(s.whatsapp)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2.5 bg-gold text-ink font-semibold px-7 py-3.5 rounded-full hover:bg-goldlight transition-all hover:scale-[1.02]"
            >
              <MessageCircle size={17} />
              {t("hero.cta_whatsapp")}
            </a>
            <button
              data-testid="hero-services-button"
              onClick={() => scrollToId("#servicios")}
              className="inline-flex items-center gap-2.5 border border-bone/25 text-bone px-7 py-3.5 rounded-full hover:border-gold hover:text-gold transition-colors"
            >
              {t("hero.cta_book")}
            </button>
            <span className="font-mono text-[11px] tracking-[0.2em] text-mut/80 uppercase flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-gold animate-pulse" />
              {t("hero.walkins")}
            </span>
          </motion.div>
        </div>

        {s.hero_logo_path && (
          <motion.div
            style={{ y: logoY }}
            initial={{ clipPath: "inset(0 0 100% 0)", opacity: 0 }}
            animate={{ clipPath: "inset(0 0 0% 0)", opacity: 1 }}
            transition={{ delay: 0.6, duration: 1.3, ease: [0.16, 1, 0.3, 1] }}
            className="hidden lg:flex justify-center items-end"
          >
            <div className="border border-gold/25 bg-[#050505] p-10 xl:p-12 relative">
              <span className="absolute -top-px left-1/2 -translate-x-1/2 h-px w-24 bg-gold" />
              <span className="absolute -bottom-px left-1/2 -translate-x-1/2 h-px w-24 bg-gold" />
              <motion.img
                src={mediaUrl(s.hero_logo_path)}
                alt="Blackbeard Barbería logo"
                className="w-60 xl:w-72"
                animate={{ y: [0, -12, 0] }}
                transition={{ repeat: Infinity, duration: 6, ease: "easeInOut" }}
              />
            </div>
          </motion.div>
        )}
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        style={{ opacity: fade }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-mut/60"
      >
        <span className="font-mono text-[10px] tracking-[0.3em] uppercase">{t("hero.scroll")}</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={15} />
        </motion.span>
      </motion.div>
    </section>
  );
};

export default Hero;
